// Notification service for imperative notification sending
// Used by context providers that can't use hooks directly

type NotificationPermission = 'default' | 'granted' | 'denied';

interface NotificationServiceState {
  permission: NotificationPermission;
  isSupported: boolean;
}

class NotificationService {
  private static instance: NotificationService;
  private state: NotificationServiceState = {
    permission: 'default',
    isSupported: false,
  };

  private constructor() {
    this.initialize();
  }

  static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  private initialize() {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      this.state.isSupported = true;
      this.state.permission = Notification.permission as NotificationPermission;
    }
  }

  refreshPermission() {
    if (this.state.isSupported) {
      this.state.permission = Notification.permission as NotificationPermission;
    }
  }

  isEnabled(): boolean {
    return this.state.isSupported && this.state.permission === 'granted';
  }

  sendNotification(title: string, options?: NotificationOptions): Notification | null {
    if (!this.isEnabled()) return null;

    try {
      const notification = new Notification(title, {
        icon: '/favicon-32.png',
        badge: '/favicon-32.png',
        ...options,
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
      };

      return notification;
    } catch (err) {
      console.error('Error sending notification:', err);
      return null;
    }
  }

  sendTimerCompleteNotification(taskName?: string) {
    return this.sendNotification(
      '⏰ Focus session complete!',
      {
        body: taskName 
          ? `Great work on "${taskName}". Time to take a break.`
          : 'Time to take a well-deserved break.',
        tag: 'timer-complete',
        requireInteraction: true,
      }
    );
  }

  sendBreakOverNotification(taskName?: string) {
    return this.sendNotification(
      '☕ Break is over!',
      {
        body: taskName 
          ? `Ready to get back to "${taskName}"?`
          : 'Ready to get back to focus mode?',
        tag: 'break-over',
        requireInteraction: true,
      }
    );
  }

  sendTaskDueNotification(taskName: string) {
    return this.sendNotification(
      '📅 Task due soon',
      {
        body: `"${taskName}" is due in 30 minutes`,
        tag: `task-due-${taskName}`,
        requireInteraction: false,
      }
    );
  }
}

export const notificationService = NotificationService.getInstance();
